import { useEffect, useState, type RefObject } from "react";

/** Tracks whether a vertically scrollable container has hidden content above or below. */
export function useVerticalScrollHints(scrollerRef: RefObject<HTMLElement | null>) {
  const [hints, setHints] = useState({ top: false, bottom: false });

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const update = () => {
      const maxScroll = el.scrollHeight - el.clientHeight;
      const canScroll = maxScroll > 2;
      setHints({
        top: canScroll && el.scrollTop > 2,
        bottom: canScroll && el.scrollTop < maxScroll - 2
      });
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      window.addEventListener("resize", update);
      let mo: MutationObserver | undefined;
      if (typeof MutationObserver !== "undefined") {
        mo = new MutationObserver(update);
        mo.observe(el, { childList: true, subtree: true });
      }
      return () => {
        el.removeEventListener("scroll", update);
        ro.disconnect();
        mo?.disconnect();
        window.removeEventListener("resize", update);
      };
    }
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [scrollerRef]);

  return hints;
}
